import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { grandPrixConfigApi } from '../api';
import type {
  GrandPrixCardMap,
  GrandPrixCardPatchPayload,
  GrandPrixSlot,
  MatchesByStage,
} from '../types';
import { getDefaultCardMap, getErrorMessage } from '../utils';

type State = {
  cards: GrandPrixCardMap;
  matchesByStage: MatchesByStage | null;
};

export function useGrandPrixConfig() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<State>({
    cards: getDefaultCardMap(),
    matchesByStage: null,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const [cardsResult, matchesResult] = await Promise.allSettled([
        grandPrixConfigApi.getCards(controller.signal),
        grandPrixConfigApi.getAvailableMatches(controller.signal),
      ]);

      if (!mountedRef.current) return;

      setData((prev) => ({
        cards: cardsResult.status === 'fulfilled' ? cardsResult.value : prev.cards,
        matchesByStage:
          matchesResult.status === 'fulfilled' ? matchesResult.value : prev.matchesByStage,
      }));

      const failed = [cardsResult, matchesResult].find((item) => item.status === 'rejected');

      if (failed && failed.status === 'rejected') {
        setError(new Error(getErrorMessage(failed.reason)));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const patchCard = useCallback(
    async (slot: GrandPrixSlot, payload: GrandPrixCardPatchPayload) => {
      setSaving(true);

      try {
        return await grandPrixConfigApi.patchCard(slot, payload);
      } finally {
        if (mountedRef.current) {
          setSaving(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    mountedRef.current = true;

    fetchData();

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      ...data,
      loading,
      saving,
      error,
      refetch: fetchData,
      patchCard,
    }),
    [data, error, fetchData, loading, patchCard, saving]
  );
}
